import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import './letterGlas.css'

export function LetterGlasRail({
  letters,
  have,
  letter,
  hoverLetter,
  focusLetter,
  ariaLabelPrefix = 'Nachname',
  onHover,
  onPick,
}: {
  letters: string[]
  have: Set<string>
  letter: string | null
  hoverLetter: string | null
  focusLetter: string | null
  ariaLabelPrefix?: string
  onHover: (L: string | null) => void
  onPick: (fn: (prev: string | null) => string | null) => void
}) {
  const railRef = useRef<HTMLDivElement>(null)
  const [loupeY, setLoupeY] = useState(0)
  const [loupeShow, setLoupeShow] = useState(false)

  useLayoutEffect(() => {
    const root = railRef.current
    if (!root || !focusLetter) {
      setLoupeShow(false)
      return
    }
    const btn = root.querySelector(`[data-letter="${focusLetter}"]`) as HTMLElement | null
    if (!btn) {
      setLoupeShow(false)
      return
    }
    setLoupeY(btn.offsetTop + btn.offsetHeight / 2 - 34)
    setLoupeShow(true)
  }, [focusLetter, letters, have])

  const focusIdx = focusLetter ? letters.indexOf(focusLetter) : -1

  return (
    <div ref={railRef} className="az-glas" onMouseLeave={() => onHover(null)}>
      <div
        className={`az-glas-loupe${loupeShow ? ' show' : ''}${letter && !hoverLetter ? ' lock' : ''}`}
        style={loupeShow ? { transform: `translateY(${loupeY}px) scale(1)` } : undefined}
        aria-hidden="true"
      >
        <b>{focusLetter ?? ''}</b>
      </div>
      {letters.map((L, j) => {
        const has = have.has(L)
        const on = letter === L
        const hot = focusLetter === L
        let transform = ''
        let delay = '0ms'
        let opacity: string | undefined
        if (focusIdx >= 0) {
          const d = j - focusIdx
          const dist = Math.abs(d)
          delay = `${dist * 16}ms`
          if (d !== 0 && dist <= 8) {
            const fall = Math.max(0, (9 - dist) / 8)
            const ty = Math.sign(d) * -1 * 6 * fall * fall
            transform = `translateY(${ty}px)`
            opacity = String(0.55 + 0.45 * (1 - fall * 0.25))
          }
        }
        return (
          <button
            key={L}
            type="button"
            data-letter={L}
            disabled={!has && letter !== L}
            aria-label={`${ariaLabelPrefix} ${L}`}
            aria-pressed={on}
            className={`${has ? 'has' : ''}${on ? ' on' : ''}${hot ? ' hot' : ''}`}
            style={{ transform, transitionDelay: delay, opacity }}
            onMouseEnter={() => { if (has) onHover(L) }}
            onClick={() => onPick(prev => (prev === L ? null : L))}
          >
            {L}
          </button>
        )
      })}
    </div>
  )
}

export function LetterGlasGroup({
  letter,
  hidden,
  children,
}: {
  letter: string
  hidden: boolean
  children: ReactNode
}) {
  return (
    <div
      ref={(el) => {
        if (el) el.inert = hidden
      }}
      data-letter-group={letter}
      aria-hidden={hidden}
      className={`az-glas-group${hidden ? ' out' : ''}`}
    >
      <div className="az-glas-group-inner">
        <div className="px-1.5 pb-1 pt-2 text-[11px] font-bold tracking-wider text-gray-400">{letter}</div>
        {children}
      </div>
    </div>
  )
}
